import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { get, merge } from 'lodash';
import {
  TestSetupComponent,
  ITestSetupEntry,
  BaseSetupTab,
  TestSetupTabsComponent,
  SaveTemplateComponent,
  TestSetupModule,
  TemplateService
} from '@nrwl/test-setup';
import { NavigationProvider } from '@nrwl/navigation-abstract';
import {
  DialogService,
  HelpersService,
  NotificationService,
  TestListService,
  TestTypeValue,
  ValuesExchangeProvider
} from '@nrwl/common-lib';
import { BertSetupService } from './bert-setup.service';
import { TemplateTabsComponent } from '@nrwl/template-management';
import { BertSetupComponent } from './tabs/bert-setup/bert-setup.component';
import { BertStreamsComponent } from './tabs/bert-streams/bert-streams.component';
import { BertEndpointsComponent } from './tabs/bert-endpoints/bert-endpoints.component';
import { BertConfigFieldMapping as ConfigFieldMapping } from './models/model';

@Component({
  selector: 'nrwl-bert-setup',
  standalone: true,
  imports: [TestSetupModule],
  providers: [BertSetupService],
  template: '<nrwl-test-setup-tabs [parentObj]="currentObj"></nrwl-test-setup-tabs>'
})
export class BertTestSetupComponent
  extends TestSetupComponent
  implements OnInit, AfterViewInit, OnDestroy, ITestSetupEntry
{
  @ViewChild(TestSetupTabsComponent, { static: true }) testSetupTabRef: TestSetupTabsComponent;
  templateDialogRef: MatDialogRef<TemplateTabsComponent>;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  setupConfig: any;
  agentBasedConfig: any;
  setupId: string;
  mode: string;
  templateName: string;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClose = new EventEmitter<any>();
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  dataId: any;
  router: Router;
  setupService: BertSetupService;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  openWizard: any;
  subscription: Subscription;
  changeDetectorRef: ChangeDetectorRef;
  activatedRouteSubscription: Subscription;
  editDeviceDialogRef: MatDialogRef<SaveTemplateComponent>;
  currentObj: any; //eslint-disable-line
  templateId: string;
  testId = null;
  shaId: string;
  constructor(
    router: Router,
    setupService: BertSetupService,
    changeDetectorRef: ChangeDetectorRef,
    private readonly activatedRoute: ActivatedRoute,
    private readonly navigationProvider: NavigationProvider,
    private readonly testListService: TestListService,
    private readonly exchange: ValuesExchangeProvider,
    private readonly notificationService: NotificationService,
    private helpersService: HelpersService,
    private readonly templateService: TemplateService,
    private dialog: MatDialog,
    private readonly dialogService: DialogService
  ) {
    super();
    this.router = router;
    this.setupService = setupService;
    this.changeDetectorRef = changeDetectorRef;
  }

  ngOnInit() {
    this.currentObj = this;
    if (this.mode === 'new') {
      this.setupService.getControllerConfiguration();
    }
    this.activatedRouteSubscription = this.activatedRoute.queryParams.subscribe(data => {
      if (this.testId !== this.setupId) {
        const defaultParams = merge(
          {},
          this.dataId,
          { modeType: this.mode },
          JSON.parse(get(data, 'defaultParams', '{}'))
        );
        this.templateId = defaultParams?.templateId;
        this.setupService.openTestSetup(
          this.setupId,
          this.setupConfig,
          defaultParams,
          null,
          null,
          this.agentBasedConfig
        );
        this.testId = this.setupId;
      }
    });
    if (!this.setupService.editMode) {
      this.setupService.currentConfig.buttonName = 'START TEST';
    }
    this.templateService.closeDialog?.subscribe(status => {
      if (status && this.templateDialogRef) {
        this.templateDialogRef.close();
      }
    });
  }

  ngAfterViewInit() {
    this.createContentComponent(1);
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.activatedRouteSubscription) {
      this.activatedRouteSubscription.unsubscribe();
    }
  }
  // eslint-disable-next-line
  get(object: any, path: string): any {
    return get(object, path);
  }

  //-------------- Control && Validation -----------------------
  toggleTabChange(index: number): void {
    this.setupService.toggleTabChange(index);
    const tabIndex = index + 1;
    // create the component only once when its not available
    if (!this.testSetupTabRef['contentRef' + `${tabIndex}`].length) {
      this.createContentComponent(tabIndex);
    }
  }

  toggleClose(): void {
    this.setupService.toggleClose(() => this.onClose.emit(true));
  }

  toggleReOpen(): void {
    this.setupService.reOpen();
  }

  toggleFinish(): void {
    this.createBertTest();
  }

  createBertTest(): void {
    this.setupService.onNext();
    const allTestParams = this.setupService.getAllTestParams();
    this.setupService.openTestStatusDialog(
      this.setupService.editMode ? 'Updating bert' : 'Creating bert',
      this.setupService.editMode ? 'Update' : 'Add'
    );
    const lmapObject = this.setupService.createLMAPObject(allTestParams);
    if (!this.setupService.editMode) {
      this.subscription = this.setupService._saveControllerTest(lmapObject).subscribe(
        response => {
          const message = 'BERT test has been created';
          this.notificationService.onSuccess(message);
          this.testListService.updateTestResponse();
          this.setupService.dialogRef.close();
          this.toggleClose();
          this.exchange.notifyListeners('new_test', response);
          this.openTestRunning(response.data.id);
        },
        error => {
          const message = 'Failed to create BERT test';
          this.notificationService.onFail(message);
          this.setupService.dialogRef.close();
          this.toggleClose();
        }
      );
    } else {
      this.subscription = this.setupService
        ._updateControllerTest({
          opt: { id: this.setupService.defaultParamsById['scheduleId'], apply: 'next-run' },
          config: lmapObject
        })
        .subscribe(
          response => {
            const message = 'BERT test has been updated';
            this.notificationService.onSuccess(message);
            this.testListService.updateTestResponse();
            this.setupService.dialogRef.close();
            this.toggleClose();
            this.exchange.notifyListeners('new_test', response);
          },
          error => {
            const message = 'Failed to update BERT test';
            this.notificationService.onFail(message);
            this.setupService.dialogRef.close();
            this.toggleClose();
          }
        );
    }
  }

  openTestRunning(testId: string): void {
    this.navigationProvider.navigateToDashboard(['test-running'], {
      queryParams: {
        testId: testId,
        testName: this.setupService.output.alias,
        testType: TestTypeValue.BERT,
        source: null,
        layeredSource: [],
        queryParamsHandling: 'merge',
        replaceUrl: true,
        hideTestSetup: true // to hide the testsetup wizard panel
      }
    });
  }

  downloadTestConfig(): void {
    this.setupService.onNext();
    const allTestParams = this.setupService.getAllTestParams();
    const lmapObject = this.setupService.createLMAPObject(allTestParams);
    this.setupService._downloadTestConfig(lmapObject);
  }

  saveTestConfig(): void {
    this.templateService.setTemplateId(this.templateId);
    this.setupService.onNext();
    let configParams = this.setupService.getBertParameters();
    const otherParams = {
      [ConfigFieldMapping.SCHEDULER]: this.setupService.output[ConfigFieldMapping.SCHEDULER]
    };
    configParams = { ...configParams, ...otherParams };
    const allTestParams = this.setupService._createTemplateFormat(
      configParams,
      this.setupService.selectedOriginator,
      this.setupService.selectedDestination
    );
    allTestParams['group'] = this.setupService.getStreamsGroup();
    allTestParams['engine-a-info'] = this.setupService.getEngineAInfo();
    allTestParams['engine-b-info'] = this.setupService.getEngineBInfo();
    this.editDeviceDialogRef = this.dialog.open(SaveTemplateComponent, {
      width: '70rem',
      disableClose: true,
      data: { deviceInfo: allTestParams, editMode: true, testType: TestTypeValue.BERT }
    });
  }

  openTemplates(): void {
    const param = {
      id: 'setupService.test_templates',
      callBackService: this,
      callBackFuntion: this.openTemplatesDialog
    };
    this.dialogService.showDialog('confirm', 'body', param);
  }
  // eslint-disable-next-line
  openTemplatesDialog(service: any): void {
    service.toggleClose();
    service.templateDialogRef = service.dialog.open(TemplateTabsComponent, {
      width: '100rem',
      minHeight: '50rem',
      height: 'calc(100vh - 20px)',
      disableClose: true,
      data: {
        filterType: TestTypeValue.BERT,
        myTemplatesColumnHeaders: ['name', 'accessLevel', 'lastModified', 'selectActions'],
        sharedTemplatesColumnHeaders: [
          'name',
          'accessLevel',
          'owner',
          'lastModified',
          'selectActions'
        ]
      }
    });
  }

  getSetupService(): BertSetupService {
    return this.setupService;
  }
  getSetupTab(): BaseSetupTab {
    return this.setupService.currentTab;
  }

  getContentViewRef(index: number): ViewContainerRef {
    return this.testSetupTabRef['contentRef' + `${index}`];
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  resolveComponentType(pageId: string): any {
    switch (pageId) {
      case 'bert.setup.html':
        return BertSetupComponent;
      case 'bert.endpoints.html':
        return BertEndpointsComponent;
      case 'bert.streams.html':
        return BertStreamsComponent;
      default: {
        console.error('Unrecognized template name:', pageId);
        return null;
      }
    }
  }
}
